import {
  AimOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CameraOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  ConfigProvider,
  Empty,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Tour,
  Typography,
  message,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { VIBRANT_COLORS } from "../../utils/constants";
import {
  checkProximity,
  checkProximityWithOtherZones,
  checkZoneCrossOther,
  drawArrow,
  drawCoordinates,
  drawLine,
  drawLineCoordinates,
  formatCoordinates,
} from "../../utils/functions/drawFunctions";
import {
  handleEditLine,
  handleEditZone,
  handleMoveVertice,
  handleMoveVerticeClick,
} from "../../utils/functions/editFunctions";
import useApi from "../../utils/useApi";
import CreateCamera from "./CreateCameras";
import EditZones from "./EditZones";
import ListCameras from "./ListCameras";

const { Text, Link } = Typography;

const cardOptions = [
  {
    value: "edit_zone",
    label: "Gerenciar configurações de zonas",
    icon: <SettingOutlined />,
    adminOnly: false,
  },
  {
    value: "add_cameras",
    label: "Gerenciar câmeras",
    icon: <CameraOutlined />,
    adminOnly: false,
  },
  {
    value: "calibrate_cameras",
    label: "Calibrar câmeras",
    icon: <AimOutlined />,
    adminOnly: true,
  },
];

const REFRESH_CAMERAS_MESSAGE_KEY = "refresh-camera-frames";

function ZonesManager({
  addLineZone,
  initialSelectedOption,
  initialConfigState,
  existingConfigId,
  initialCamerasToEdit,
}) {
  const calibratorRef = useRef(null);
  const calibratorOrigin = useMemo(
    () =>
      new URL(
        process.env.REACT_APP_CALIBRATOR_URL || "https://calibrator.ouran.app"
      ).origin,
    []
  );
  const sendCalibratorToken = useCallback(() => {
    const token = localStorage.getItem("access");
    const target = calibratorRef.current?.contentWindow;
    if (!token || !target) return;
    target.postMessage({ type: "ouran-auth", token }, calibratorOrigin);
  }, [calibratorOrigin]);
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== calibratorOrigin) return;
      if (event.data?.type !== "ouran-auth-request") return;
      sendCalibratorToken();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [calibratorOrigin, sendCalibratorToken]);
  const [cameras, setCameras] = useState([]);
  const [camerasToEdit, setCamerasToEdit] = useState(
    initialCamerasToEdit || []
  );

  const [selectedOption, setSelectedOption] = useState(
    initialSelectedOption || ""
  );
  const [ableToEditZones, setAbleToEditZones] = useState(false);
  const [errorTypeToEditZones, setErrorTypeToEditZones] = useState("");
  const [readyToEditZones, setReadyToEditZones] = useState(false);
  const [drawMode, setDrawMode] = useState("");
  const [isModalLinesZonesOpen, setIsModalLinesZonesOpen] = useState(false);

  const [ableToEditLines, setAbleToEditLines] = useState(false);
  const [errorTypeToEditLines, setErrorTypeToEditLines] = useState("");
  const [readyToEditLines, setReadyToEditLines] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [needUpdate, setNeedUpdate] = useState(false);

  const [isOpenModalZoneConfig, setIsOpenModalZoneConfig] = useState(false);
  const [zoneConfigs, setZoneConfigs] = useState([]);
  const [configState, setConfigState] = useState(initialConfigState || "");
  const [configDescription, setConfigDescription] = useState("");
  const [configStatus, setConfigStatus] = useState(true);
  const [isCreatingConfig, setIsCreatingConfig] = useState(false);
  const [tempConfigData, setTempConfigData] = useState(null);

  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState(null);
  const [type, setType] = useState("");
  const [tempConfigID, setTempConfigID] = useState(existingConfigId || null);

  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [camerasWithZones, setCamerasWithZones] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);

  const [zonesData, setZonesData] = useState([]);

  const [zones, setZones] = useState([]);
  const [zonestoPost, setZonestoPost] = useState([]);
  const [lines, setLines] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [zoneBeingEdited, setZoneBeingEdited] = useState(null);
  const [lineBeingEdited, setLineBeingEdited] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [backupCoord, setBackupCoord] = useState([]);

  const [isHovering, setIsHovering] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [editedZoneDescription, setEditedZoneDescription] = useState("");
  const [currentConfig, setCurrentConfig] = useState(null);

  const [coordIndex, setCoordIndex] = useState([]);
  const [pointIndex, setPointIndex] = useState([]);

  const [isPressed, setIsPressed] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const [canvas, setCanvas] = useState(null);
  const [currentCamera, setCurrentCamera] = useState(null);
  const [isAddingInView, setIsAddingInView] = useState(false);
  const [addTargetType, setAddTargetType] = useState("");
  const [newShapeStartPoint, setNewShapeStartPoint] = useState(null);
  const [newShapeCurrentPoint, setNewShapeCurrentPoint] = useState(null);
  const [newShapeSegments, setNewShapeSegments] = useState([]);
  const [newShapeColor, setNewShapeColor] = useState(VIBRANT_COLORS[0].code);
  const [awaitingArrowSelection, setAwaitingArrowSelection] = useState(false);
  const [lastLinePoints, setLastLinePoints] = useState(null);
  const [isNewShapeModalOpen, setIsNewShapeModalOpen] = useState(false);
  const [newShapeDescription, setNewShapeDescription] = useState("");
  const [pendingNewShape, setPendingNewShape] = useState(null);
  const [isSavingNewShape, setIsSavingNewShape] = useState(false);
  const [isRefreshingCameraFrames, setIsRefreshingCameraFrames] =
    useState(false);
  const isMountedRef = useRef(true);
  const viewConfigCallRef = useRef(0);

  const [shouldOpenCameraTable, setShouldOpenCameraTable] = useState(false);
  const { shouldShowTutorialBool, user } = useAuth();
  const isAdministrator = Boolean(user?.is_superuser);

  const [isFirstPhaseTutorialOpen, setIsFirstPhaseTutorialOpen] = useState(
    shouldShowTutorialBool
  );

  const [isSecondPhaseTutorialOpen, setIsSecondPhaseTutorialOpen] =
    useState(false);

  const [isThirdPhaseTutorialOpen, setIsThirdPhaseTutorialOpen] =
    useState(false);

  const [isFourthPhaseTutorialOpen, setIsFourthPhaseTutorialOpen] =
    useState(false);

  const [isFifthPhaseTutorialOpen, setIsFifthPhaseTutorialOpen] =
    useState(false);

  const [offsetGap, setOffsetGap] = useState(10);
  const tutorialRefs = {
    selector: useRef(null),
    addCamera: useRef(null),
    cameraList: useRef(null),
    addConfig: useRef(null),
    configForm: useRef(null),
    saveConfig: useRef(null),
    zoneOptions: useRef(null),
    cameraSelector: useRef(null),
  };

  const steps = [
    {
      title: "2º passo: Abrir menu de câmeras e zonas",
      description:
        "Clique no seletor para abrir o menu e selecione a opção 'Gerenciar câmeras'.",
      nextButtonProps: {
        children: "Próximo",
        onClick: () => {
          setSelectedOption("add_cameras");
        },
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setShouldOpenCameraTable(false);
        },
      },
      target: () => tutorialRefs.selector.current,
    },

    {
      title: "3º passo: Adicionar câmeras",
      description: "Preecha o formulário para adicionar uma nova câmera",
      nextButtonProps: {
        children: "Próximo",
        onClick: () => {
          setShouldOpenCameraTable(true);
          setOffsetGap([10, 65]);
        },
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setShouldOpenCameraTable(false);
          setOffsetGap([0, 10]);
        },
      },
      target: () => tutorialRefs.addCamera.current,
    },
    {
      title: "4º passo: confira suas câmeras cadastradas",
      description: "Confira todas as informações e revise se necessário",
      target: () => tutorialRefs.cameraList.current,
      nextButtonProps: {
        children: "Próximo",
        onClick: () => {
          setOffsetGap([10, 10]);
        },
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setShouldOpenCameraTable(false);
          setOffsetGap([0, 10]);
        },
      },
    },
    {
      title: "5º passo: Adicione configurações e zonas as câmeras",
      description:
        "Agora que você já cadastrou suas câmeras, clique no seletor e selecione 'Gerenciar configuração de zonas'.",
      target: () => tutorialRefs.selector.current,
      nextButtonProps: {
        children: "Finalizar",
        onClick: () => {},
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setShouldOpenCameraTable(true);
        },
      },
    },

    // ==============================================================================================================
    {
      title: "6º passo: Adicione uma nova configuração",
      description:
        "Crie uma nova configuração de zonas para adicionar as câmeras, ela são necessárias para você conseguir trocar rapidamente entre um padrão de zonas para o outro",
      target: () => tutorialRefs.addConfig.current,
      nextButtonProps: {
        children: "Próximo",
        onClick: () => {
          setIsOpenModalZoneConfig(true);
        },
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setSelectedOption("edit_zone");
        },
      },
    },
    {
      title: "7º passo: Cadastre uma nova configuração",
      description:
        "Preencha as informações necessárias para criar uma nova configuração de zonas",
      target: () => tutorialRefs.configForm.current,
      nextButtonProps: {
        children: "Finalizar",
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setIsOpenModalZoneConfig(true);
        },
      },
    },
    {
      title: "8º passo: Salve a configuração",
      description: "Clique no botão para salvar a configuração criada",
      target: () => tutorialRefs.saveConfig.current,
      nextButtonProps: {
        children: "Próximo",
        onClick: () => {
          setOffsetGap([10, 10]);
          setIsThirdPhaseTutorialOpen(true);
        },
      },
      prevButtonProps: {
        children: "Anterior",
        onClick: () => {
          setOffsetGap([10, 10]);
        },
      },
    },
    {
      title: "9º passo: Crie uma zona ou linha",
      description:
        "Uma zona metrifica quanto tempo um cliente fica contido nessa área, enquanto a linha somente marca uma linha de entrada que conta quantas pessoas passam por ela.",
      target: () => tutorialRefs.zoneOptions.current,
      nextButtonProps: {
        children: "Ok",
      },
      prevButtonProps: {
        children: "Anterior",
        disabled: true,
      },
    },
    {
      title: "10º passo: Selecione as cameras",
      description:
        "Selecione as cameras que você deseja editar e clique em 'Iniciar edição' para começar a desenhar as zonas ou linhas.",
      target: () => tutorialRefs.cameraSelector.current,
      nextButtonProps: {
        children: "Ok",
      },
    },
  ];

  const [mode, setMode] = useState("");

  const canvasRef = useRef(null);

  const {
    getCollections,
    getCameras,
    getZoneConfigs,
    updateZoneConfigs,
    postConfigs,
    retrieve_zones_by_config,
    retrieve_lines_by_config,
    createZoneConfig,
    deactivateOtherConfigs,
    removeZoneFromConfig,
    removeLineFromConfig,
    getZones,
    getLines,
    postZones,
    postLines,
    refreshConfigCameraFrames,
    updateZonesInCameras,
    updateLinesInCameras,
    patchZoneIsStaff,
    patchZoneIsFila,
    updateZoneCoordinates,
    updateLineCoordinates,
    duplicateConfig,
  } = useApi();

  const sortedData = [...zoneConfigs].sort((a, b) => {
    const creationDiff =
      new Date(b.creation_date).getTime() - new Date(a.creation_date).getTime();
    if (creationDiff !== 0) {
      return creationDiff;
    }
    return b.id - a.id;
  });

  const handleOption1 = () => {
    setIsModalLinesZonesOpen(false);
    setDrawMode("line");
    setIsFourthPhaseTutorialOpen(true);
    setIsThirdPhaseTutorialOpen(false);
  };

  const handleOption2 = () => {
    setIsModalLinesZonesOpen(false);
    setDrawMode("zone");
    setIsFourthPhaseTutorialOpen(true);
    setIsThirdPhaseTutorialOpen(false);
  };

  const handleDeleteZone = async () => {
    if (!zoneToDelete) return;

    const deleteActions = {
      zone: (record) =>
        removeZoneFromConfig(record.id, record.configuration, record.camera_id),
      line: (record) =>
        removeLineFromConfig(record.id, record.configuration, record.camera_id),
    };

    const deleteAction = deleteActions[type];
    if (!deleteAction) return;

    try {
      await deleteAction(zoneToDelete);
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
    } finally {
      setIsModalDeleteOpen(false);
      setZoneToDelete(null);
      viewConfig(tempConfigID, true);
    }
  };

  const viewConfig = async (id, preserveCameraIndex = false) => {
    const callId = ++viewConfigCallRef.current;
    try {
      const camerasResponse = await getCameras();
      const allCameras = camerasResponse.data;

      const zonesResponse = await retrieve_zones_by_config(id);
      const linesResponse = await retrieve_lines_by_config(id);

      if (callId !== viewConfigCallRef.current) return;

      const camerasWithZonesAndLines = allCameras.map((camera) => {
        const matchingZones = zonesResponse.find(
          (c) => c.camera_id === camera.id
        );
        const matchingLines = linesResponse.find(
          (c) => c.camera_id === camera.id
        );

        return {
          camera_id: camera.id,
          camera_name: camera.name || "",
          camera_image: camera.image,
          zones: matchingZones ? matchingZones.zones : [],
          lines: matchingLines ? matchingLines.lines : [],
        };
      });

      if (!camerasWithZonesAndLines.length) {
        setCamerasWithZones([]);
        setZonesData([]);
        setSelectedCameraId(null);
        return;
      }

      const targetIndex = preserveCameraIndex
        ? Math.min(currentCameraIndex, camerasWithZonesAndLines.length - 1)
        : 0;

      setCamerasWithZones(camerasWithZonesAndLines);
      setCurrentCameraIndex(targetIndex);

      const formattedData = camerasWithZonesAndLines.flatMap((camera) => [
        ...camera.zones.map((zone) => ({
          id: zone.zone_id,
          type: "zone",
          configuration: zone.configuration,
          description: zone.description,
          identification: zone.color,
          camera_id: camera.camera_id,
          coordinates: zone.vertices,
          is_staff_zone: zone.is_staff_zone || false,
          is_fila_zone: zone.is_fila_zone || false,
        })),
        ...camera.lines.map((line) => ({
          id: line.line_id,
          type: "line",
          configuration: line.configuration,
          description: line.description,
          identification: line.color,
          camera_id: camera.camera_id,
          coordinates: line.vertices,
        })),
      ]);

      setZonesData(formattedData);

      await showZonesOnMap([camerasWithZonesAndLines[targetIndex]]);
    } catch (error) {
      console.error("Erro ao carregar câmeras, zonas ou linhas:", error);
    }
  };

  const refreshCamerasInOpenedConfig = async () => {
    if (!tempConfigID || isRefreshingCameraFrames) return;
    if (!isMountedRef.current) return;

    setIsRefreshingCameraFrames(true);
    message.open({
      key: REFRESH_CAMERAS_MESSAGE_KEY,
      type: "loading",
      content: "Atualizando imagens das cameras...",
      duration: 0,
    });

    try {
      const response = await refreshConfigCameraFrames(tempConfigID, 240000);
      const capturedCount = response?.data?.captured_count ?? 0;
      const failedCount = response?.data?.failed_count ?? 0;
      if (!isMountedRef.current) return;

      if (failedCount > 0) {
        message.open({
          key: REFRESH_CAMERAS_MESSAGE_KEY,
          type: "warning",
          content: `Atualizacao concluida: ${capturedCount} cameras atualizadas e ${failedCount} falhas.`,
          duration: 4,
        });
      } else {
        message.open({
          key: REFRESH_CAMERAS_MESSAGE_KEY,
          type: "success",
          content: `Atualizacao concluida: ${capturedCount} cameras atualizadas.`,
          duration: 4,
        });
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Erro ao atualizar capturas das cameras:", error);
      const isTimeout =
        error?.code === "ECONNABORTED" ||
        String(error?.message || "")
          .toLowerCase()
          .includes("timeout");
      message.open({
        key: REFRESH_CAMERAS_MESSAGE_KEY,
        type: isTimeout ? "warning" : "error",
        content: isTimeout
          ? "Tempo limite para confirmacao excedido. A atualizacao pode ter sido concluida; confira as imagens."
          : "Nao foi possivel atualizar as imagens das cameras.",
        duration: 5,
      });
    } finally {
      if (isMountedRef.current) {
        setIsRefreshingCameraFrames(false);
      }
    }
    if (isMountedRef.current) {
      await viewConfig(tempConfigID, true);
    }
  };

  const handleRefreshButtonMouseEnter = (event) => {
    event.currentTarget.style.backgroundColor = "#73d13d";
    event.currentTarget.style.borderColor = "#73d13d";
    event.currentTarget.style.transform = "translateY(-1px)";
    event.currentTarget.style.boxShadow = "0 4px 12px rgba(82, 196, 26, 0.35)";
  };

  const handleRefreshButtonMouseLeave = (event) => {
    event.currentTarget.style.backgroundColor = "#52c41a";
    event.currentTarget.style.borderColor = "#52c41a";
    event.currentTarget.style.transform = "translateY(0)";
    event.currentTarget.style.boxShadow = "none";
  };

  const handleRefreshButtonMouseDown = (event) => {
    event.currentTarget.style.backgroundColor = "#389e0d";
    event.currentTarget.style.borderColor = "#389e0d";
    event.currentTarget.style.transform = "translateY(0) scale(0.99)";
    event.currentTarget.style.boxShadow = "0 2px 6px rgba(82, 196, 26, 0.3)";
  };

  const handleRefreshButtonMouseUp = (event) => {
    event.currentTarget.style.backgroundColor = "#73d13d";
    event.currentTarget.style.borderColor = "#73d13d";
    event.currentTarget.style.transform = "translateY(-1px)";
    event.currentTarget.style.boxShadow = "0 4px 12px rgba(82, 196, 26, 0.35)";
  };

  const duplicateconfig = async (id) => {
    try {
      await duplicateConfig(id);

      setTimeout(async () => {
        const response = await getZoneConfigs();
        setZoneConfigs(response.data);
      }, 500); // Espera 500ms antes de buscar os novos dados
    } catch (error) {
      console.error("Erro ao duplicar configuração:", error);
      throw error;
    }
  };

  const resetNewShapeDraft = useCallback(() => {
    setNewShapeStartPoint(null);
    setNewShapeCurrentPoint(null);
    setNewShapeSegments([]);
    setAwaitingArrowSelection(false);
    setLastLinePoints(null);
  }, []);

  const stopAddingInCurrentView = useCallback(() => {
    resetNewShapeDraft();
    setIsAddingInView(false);
    setAddTargetType("");
    setDrawMode("");
    setPendingNewShape(null);
    setNewShapeDescription("");
    setIsNewShapeModalOpen(false);
  }, [resetNewShapeDraft]);

  const getNextShapeColor = useCallback(
    (targetType) => {
      const sameTypeCount = zonesData.filter(
        (item) =>
          item.camera_id === selectedCameraId && item.type === targetType
      ).length;
      return VIBRANT_COLORS[sameTypeCount % VIBRANT_COLORS.length].code;
    },
    [zonesData, selectedCameraId]
  );

  const startAddToCurrentConfig = (targetDrawMode) => {
    if (!tempConfigID) {
      message.warning("Selecione uma configuração antes de adicionar.");
      return;
    }

    if (!selectedCameraId) {
      message.warning("Selecione uma câmera antes de adicionar.");
      return;
    }

    resetNewShapeDraft();
    setEditMode(false);
    setZoneBeingEdited(null);
    setLineBeingEdited(null);
    setIsAddingInView(true);
    setAddTargetType(targetDrawMode);
    setDrawMode(targetDrawMode);
    setNewShapeColor(getNextShapeColor(targetDrawMode));
  };

  const showPreviousCamera = () => {
    if (camerasWithZones.length === 0) return;

    const previousIndex =
      (currentCameraIndex - 1 + camerasWithZones.length) %
      camerasWithZones.length;
    setCurrentCameraIndex(previousIndex);
    showZonesOnMap([camerasWithZones[previousIndex]]);
  };

  const showNextCamera = () => {
    if (camerasWithZones.length === 0) return;

    const nextIndex = (currentCameraIndex + 1) % camerasWithZones.length;
    setCurrentCameraIndex(nextIndex);
    showZonesOnMap([camerasWithZones[nextIndex]]);
  };

  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isEditingLoading, setIsEditingLoading] = useState(false);

  const showZonesOnMap = async (camerasWithZones) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve();
        return;
      }
      setCanvas(canvas);
      const context = canvas.getContext("2d");

      const camera = camerasWithZones[0];
      if (!camera) {
        setIsImageLoading(false);
        resolve();
        return;
      }

      setCurrentCamera(camera);
      setSelectedCameraId(camera.camera_id);

      if (!camera.camera_image) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        setIsImageLoading(false);
        resolve();
        return;
      }

      const imageUrl = `data:image/jpeg;base64,${camera.camera_image}`;
      const img = new Image();
      img.src = imageUrl;

      setIsImageLoading(true);
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0, canvas.width, canvas.height);

        camera.zones.forEach((zone) => {
          drawPolygon(
            context,
            zone.color,
            zone.vertices,
            canvas.width,
            canvas.height,
            false
          );
        });

        camera.lines.forEach((line) => {
          drawPolygon(
            context,
            line.color,
            line.vertices,
            canvas.width,
            canvas.height,
            true
          );
        });
        setIsImageLoading(false);
        resolve();
      };

      img.onerror = (err) => {
        console.error("Erro ao carregar a imagem:", err);
        setIsImageLoading(false);
        resolve();
      };
    });
  };

  const filteredZonesAndLines = zonesData.filter(
    (item) => item.camera_id === selectedCameraId
  );

  const drawPolygon = (
    context,
    color,
    coordinates,
    canvasWidth,
    canvasHeight,
    isLine
  ) => {
    if (!coordinates || coordinates.length < 1) return;

    context.lineWidth = 3;
    context.strokeStyle = color;
    context.lineCap = "round";

    context.beginPath();

    if (isLine) {
      if (coordinates.length < 2) {
        console.warn("Line coordinates must have at least 2 points");
        return;
      }

      try {
        const point1 = Array.isArray(coordinates[0])
          ? coordinates[0]
          : [coordinates[0].x, coordinates[0].y];
        const point2 = Array.isArray(coordinates[1])
          ? coordinates[1]
          : [coordinates[1].x, coordinates[1].y];

        const x1 = point1[0] * canvasWidth;
        const y1 = point1[1] * canvasHeight;
        const x2 = point2[0] * canvasWidth;
        const y2 = point2[1] * canvasHeight;

        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
      } catch (error) {
        console.error(
          "Error drawing line:",
          error,
          "Coordinates:",
          coordinates
        );
        return;
      }
    } else {
      coordinates.forEach(([xNorm, yNorm], index) => {
        const x = xNorm * canvasWidth;
        const y = yNorm * canvasHeight;

        if (index === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      });
      context.closePath();
    }

    context.stroke();
  };

  const getCurrentCameraConfig = useCallback(
    (cameraId = selectedCameraId) =>
      camerasWithZones.find((camera) => camera.camera_id === cameraId) || null,
    [camerasWithZones, selectedCameraId]
  );

  const getDirectionArrows = useCallback((first, second, arrowLength = 30) => {
    const midX = (first.x + second.x) / 2;
    const midY = (first.y + second.y) / 2;
    const angle = Math.atan2(second.y - first.y, second.x - first.x);

    return {
      arrow1: {
        x: midX + Math.cos(angle + Math.PI / 2) * arrowLength,
        y: midY + Math.sin(angle + Math.PI / 2) * arrowLength,
      },
      arrow2: {
        x: midX + Math.cos(angle - Math.PI / 2) * arrowLength,
        y: midY + Math.sin(angle - Math.PI / 2) * arrowLength,
      },
    };
  }, []);

  const drawCurrentConfigOnCanvas = useCallback(
    ({ previewPoint = null } = {}) => {
      if (!canvasRef.current) return;

      const canvasElement = canvasRef.current;
      const context = canvasElement.getContext("2d");
      context.clearRect(0, 0, canvasElement.width, canvasElement.height);

      const currentCameraConfig = getCurrentCameraConfig();
      if (!currentCameraConfig) return;

      currentCameraConfig.zones.forEach((zone) => {
        drawPolygon(
          context,
          zone.color,
          zone.vertices,
          canvasElement.width,
          canvasElement.height,
          false
        );
      });

      currentCameraConfig.lines.forEach((line) => {
        drawPolygon(
          context,
          line.color,
          line.vertices,
          canvasElement.width,
          canvasElement.height,
          true
        );
      });

      if (!isAddingInView) return;

      newShapeSegments.forEach(([start, end]) => {
        drawLine(context, newShapeColor, start.x, start.y, end.x, end.y);
      });

      if (
        addTargetType === "zone" &&
        newShapeCurrentPoint &&
        previewPoint &&
        !awaitingArrowSelection
      ) {
        drawLine(
          context,
          newShapeColor,
          newShapeCurrentPoint.x,
          newShapeCurrentPoint.y,
          previewPoint.x,
          previewPoint.y
        );
      }

      if (
        addTargetType === "line" &&
        newShapeStartPoint &&
        previewPoint &&
        !awaitingArrowSelection &&
        newShapeSegments.length === 0
      ) {
        drawLine(
          context,
          newShapeColor,
          newShapeStartPoint.x,
          newShapeStartPoint.y,
          previewPoint.x,
          previewPoint.y
        );
      }

      if (
        addTargetType === "line" &&
        awaitingArrowSelection &&
        lastLinePoints
      ) {
        const { arrow1, arrow2 } = getDirectionArrows(
          lastLinePoints.first,
          lastLinePoints.second
        );
        const angle = Math.atan2(
          lastLinePoints.second.y - lastLinePoints.first.y,
          lastLinePoints.second.x - lastLinePoints.first.x
        );
        drawArrow(context, arrow1.x, arrow1.y, angle + Math.PI / 2, "#2979FF");
        drawArrow(context, arrow2.x, arrow2.y, angle - Math.PI / 2, "#8F00FF");
      }
    },
    [
      addTargetType,
      awaitingArrowSelection,
      getCurrentCameraConfig,
      getDirectionArrows,
      isAddingInView,
      lastLinePoints,
      newShapeColor,
      newShapeCurrentPoint,
      newShapeSegments,
      newShapeStartPoint,
      drawPolygon,
    ]
  );

  const getCurrentCameraZonesForValidation = useCallback(() => {
    if (!canvasRef.current) return [];

    return zonesData
      .filter(
        (item) => item.type === "zone" && item.camera_id === selectedCameraId
      )
      .map((zone) => {
        const vertices = Array.isArray(zone.coordinates)
          ? zone.coordinates
          : [];
        const points = vertices.map(([xNorm, yNorm]) => ({
          x: xNorm * canvasRef.current.width,
          y: yNorm * canvasRef.current.height,
        }));

        const coordinates = [];
        for (let i = 0; i < points.length; i++) {
          const currentPoint = points[i];
          const nextPoint = points[(i + 1) % points.length];
          coordinates.push([currentPoint, nextPoint]);
        }

        return {
          id: zone.id,
          coordinates,
        };
      })
      .filter((zone) => zone.coordinates.length > 0);
  }, [zonesData, selectedCameraId]);

  const getCanvasCoordinates = useCallback((event) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    return formatCoordinates(
      canvasRef.current,
      rect,
      event.clientX,
      event.clientY
    );
  }, []);

  const handleOpenNewShapeModal = useCallback((shapeData) => {
    setPendingNewShape(shapeData);
    setNewShapeDescription("");
    setIsNewShapeModalOpen(true);
  }, []);

  const handleAddShapeMouseMove = useCallback(
    (event) => {
      if (!isAddingInView || editMode || awaitingArrowSelection) return;
      if (!newShapeStartPoint && !newShapeCurrentPoint) return;

      const currentCoordinates = getCanvasCoordinates(event);
      if (!currentCoordinates) return;

      drawCurrentConfigOnCanvas({ previewPoint: currentCoordinates });
    },
    [
      isAddingInView,
      editMode,
      awaitingArrowSelection,
      newShapeStartPoint,
      newShapeCurrentPoint,
      drawCurrentConfigOnCanvas,
      getCanvasCoordinates,
    ]
  );

  const handleAddShapeClick = useCallback(
    (event) => {
      const currentCoordinates = getCanvasCoordinates(event);
      if (!currentCoordinates || !isAddingInView) return;

      if (addTargetType === "zone") {
        if (!newShapeStartPoint) {
          setNewShapeStartPoint(currentCoordinates);
          setNewShapeCurrentPoint(currentCoordinates);
          setNewShapeSegments([]);
          return;
        }

        if (
          checkProximity(
            newShapeStartPoint.x,
            newShapeStartPoint.y,
            currentCoordinates.x,
            currentCoordinates.y
          )
        ) {
          if (newShapeSegments.length < 2) {
            message.warning("A zona precisa ter ao menos 3 lados.");
            return;
          }

          const closedSegments = [
            ...newShapeSegments,
            [newShapeCurrentPoint, newShapeStartPoint],
          ];
          const createdZone = {
            id: "new-zone",
            coordinates: closedSegments,
          };
          const existingZones = getCurrentCameraZonesForValidation();
          const zonesToValidate = [...existingZones, createdZone];

          if (checkZoneCrossOther(createdZone, zonesToValidate)) {
            message.warning("A nova zona cruza outra zona. Desenhe novamente.");
            resetNewShapeDraft();
            drawCurrentConfigOnCanvas();
            return;
          }

          handleOpenNewShapeModal({
            type: "zone",
            coordinates: closedSegments,
          });
          resetNewShapeDraft();
          drawCurrentConfigOnCanvas();
          return;
        }

        const snappedCoordinates = { ...currentCoordinates };
        checkProximityWithOtherZones(
          snappedCoordinates,
          getCurrentCameraZonesForValidation(),
          -1
        );

        setNewShapeSegments((previousSegments) => [
          ...previousSegments,
          [newShapeCurrentPoint, snappedCoordinates],
        ]);
        setNewShapeCurrentPoint(snappedCoordinates);
        return;
      }

      if (addTargetType === "line") {
        if (awaitingArrowSelection && lastLinePoints) {
          const { arrow1, arrow2 } = getDirectionArrows(
            lastLinePoints.first,
            lastLinePoints.second
          );

          const distToArrow1 = Math.sqrt(
            Math.pow(currentCoordinates.x - arrow1.x, 2) +
              Math.pow(currentCoordinates.y - arrow1.y, 2)
          );
          const distToArrow2 = Math.sqrt(
            Math.pow(currentCoordinates.x - arrow2.x, 2) +
              Math.pow(currentCoordinates.y - arrow2.y, 2)
          );

          if (distToArrow1 < 25 || distToArrow2 < 25) {
            const selectedArrowIndex = distToArrow1 < distToArrow2 ? 1 : 2;
            handleOpenNewShapeModal({
              type: "line",
              first: lastLinePoints.first,
              second: lastLinePoints.second,
              selectedArrow: selectedArrowIndex,
            });
            resetNewShapeDraft();
            drawCurrentConfigOnCanvas();
          }
          return;
        }

        if (!newShapeStartPoint) {
          setNewShapeStartPoint(currentCoordinates);
          setNewShapeSegments([]);
          return;
        }

        const firstPoint = newShapeStartPoint;
        setNewShapeSegments([[firstPoint, currentCoordinates]]);
        setLastLinePoints({
          first: firstPoint,
          second: currentCoordinates,
        });
        setNewShapeStartPoint(null);
        setAwaitingArrowSelection(true);
      }
    },
    [
      isAddingInView,
      addTargetType,
      newShapeStartPoint,
      newShapeSegments,
      newShapeCurrentPoint,
      awaitingArrowSelection,
      lastLinePoints,
      getCurrentCameraZonesForValidation,
      getDirectionArrows,
      handleOpenNewShapeModal,
      resetNewShapeDraft,
      drawCurrentConfigOnCanvas,
      getCanvasCoordinates,
    ]
  );

  const getNormalizedLineDirection = useCallback(
    (first, second, selectedArrowIndex, width, height) => {
      if (!selectedArrowIndex) return null;

      const angle = Math.atan2(second.y - first.y, second.x - first.x);
      const midX = (first.x + second.x) / 2;
      const midY = (first.y + second.y) / 2;
      const arrowLength = 36;

      const arrow1 = [
        (midX + Math.cos(angle + Math.PI / 2) * arrowLength) / width,
        (midY + Math.sin(angle + Math.PI / 2) * arrowLength) / height,
      ];
      const arrow2 = [
        (midX + Math.cos(angle - Math.PI / 2) * arrowLength) / width,
        (midY + Math.sin(angle - Math.PI / 2) * arrowLength) / height,
      ];

      return selectedArrowIndex === 1 ? [arrow2, arrow1] : [arrow1, arrow2];
    },
    []
  );

  const handleSaveNewShape = useCallback(async () => {
    if (
      !pendingNewShape ||
      !tempConfigID ||
      !selectedCameraId ||
      !canvasRef.current
    ) {
      return;
    }

    const canvasElement = canvasRef.current;
    const description = newShapeDescription.trim();
    const cameraKey = String(selectedCameraId);

    setIsSavingNewShape(true);
    try {
      if (pendingNewShape.type === "zone") {
        const zonePayload = {
          [cameraKey]: [
            {
              coordinates: pendingNewShape.coordinates.map((line) => [
                line[0].x / canvasElement.width,
                line[0].y / canvasElement.height,
              ]),
              description:
                description ||
                `Zona ${
                  filteredZonesAndLines.filter((item) => item.type === "zone")
                    .length + 1
                }`,
              color: newShapeColor,
              configuration: tempConfigID,
            },
          ],
        };

        const postedZones = await postZones(zonePayload);
        await updateZonesInCameras(postedZones.data);
        message.success("Zona adicionada com sucesso.");
      } else {
        const { first, second, selectedArrow } = pendingNewShape;
        const linePayload = {
          [cameraKey]: [
            {
              coordinates: [
                [first.x / canvasElement.width, first.y / canvasElement.height],
                [
                  second.x / canvasElement.width,
                  second.y / canvasElement.height,
                ],
              ],
              direction: getNormalizedLineDirection(
                first,
                second,
                selectedArrow,
                canvasElement.width,
                canvasElement.height
              ),
              description:
                description ||
                `Linha ${
                  filteredZonesAndLines.filter((item) => item.type === "line")
                    .length + 1
                }`,
              color: newShapeColor,
              configuration: tempConfigID,
            },
          ],
        };

        const postedLines = await postLines(linePayload);
        await updateLinesInCameras(postedLines.data);
        message.success("Linha adicionada com sucesso.");
      }

      setIsNewShapeModalOpen(false);
      setPendingNewShape(null);
      setNewShapeDescription("");
      await viewConfig(tempConfigID, true);
      setNewShapeColor(getNextShapeColor(addTargetType));
    } catch (error) {
      console.error("Erro ao adicionar item na configuração:", error);
      message.error("Não foi possível adicionar o item.");
    } finally {
      setIsSavingNewShape(false);
    }
  }, [
    pendingNewShape,
    tempConfigID,
    selectedCameraId,
    newShapeDescription,
    filteredZonesAndLines,
    newShapeColor,
    postZones,
    postLines,
    updateZonesInCameras,
    updateLinesInCameras,
    getNormalizedLineDirection,
    addTargetType,
    getNextShapeColor,
    viewConfig,
  ]);

  useEffect(() => {
    if (mode === "view" && !editMode) {
      drawCurrentConfigOnCanvas();
    }
  }, [mode, editMode, drawCurrentConfigOnCanvas]);

  const infoText = (id) => {
    const index = zoneConfigs.findIndex((zoneConfig) => zoneConfig.id === id);
    const zoneConfig = zoneConfigs[index];

    const creationDate = new Date(zoneConfig.creation_date).toLocaleDateString(
      "pt-BR"
    );
    const lastEditDate = new Date(zoneConfig.last_update).toLocaleDateString(
      "pt-BR"
    );

    if (zoneConfig) {
      return `A configuração ${zoneConfig.description} foi criada em ${creationDate} e editada pela última vez em ${lastEditDate}.`;
    }
  };

  const updateZones = async (zones) => {
    if (!zones) return;

    setIsEditingLoading(true);

    function formatCoordinates(coordinates, width, height) {
      if (drawMode === "line") {
        // Format line coordinates
        return coordinates.map((coord) => [
          coord[0] / width,
          coord[1] / height,
        ]);
      } else {
        // Format zone coordinates
        return coordinates.map((coordinate) => [
          coordinate[0].x / width,
          coordinate[0].y / height,
        ]);
      }
    }

    try {
      const formattedZones = {
        ...zones,
        coordinates: formatCoordinates(
          zones.coordinates,
          canvas.width,
          canvas.height
        ),
      };

      if (drawMode === "zone") {
        await updateZoneCoordinates(formattedZones);
      } else {
        await updateLineCoordinates(formattedZones);
      }

      await viewConfig(tempConfigID, true);
    } catch (error) {
      console.error(
        "Erro ao atualizar as coordenadas da zona ou linha:",
        error
      );
      throw error;
    } finally {
      setIsEditingLoading(false);
    }
  };

  // Add loading state for cameras
  const [isCamerasLoading, setIsCamerasLoading] = useState(true);

  const apiRef = useRef({});

  useEffect(() => {
    apiRef.current = {
      getCameras,
      getCollections,
      getZoneConfigs,
      getLines,
      getZones,
    };
  }, [getCameras, getCollections, getZoneConfigs, getLines, getZones]);

  useEffect(() => {
    setIsLoading(true);
    setIsCamerasLoading(true);
    setAbleToEditZones(false);
    setReadyToEditZones(false);
    setErrorTypeToEditZones("");
    setAbleToEditLines(false);
    setReadyToEditLines(false);
    setErrorTypeToEditLines("");
    setCamerasToEdit(initialCamerasToEdit || []);

    const loadData = async () => {
      try {
        const response = await apiRef.current.getCameras();
        setCameras(response.data);
        setIsCamerasLoading(false);

        if (response.data.length) {
          setAbleToEditLines(true);
          try {
            const collectionsResponse = await apiRef.current.getCollections();
            if (collectionsResponse.data.length) {
              setAbleToEditZones(true);
            } else {
              setErrorTypeToEditZones("collections");
            }
          } catch (error) {
            console.error("Error fetching collections:", error);
          }
        } else {
          setErrorTypeToEditZones("câmeras");
          setErrorTypeToEditLines("câmeras");
        }
      } catch (error) {
        console.error("Error fetching cameras:", error);
      } finally {
        setIsLoading(false);
        setIsCamerasLoading(false);
      }
    };

    loadData();
    if (selectedOption === "edit_zone") {
      if (addLineZone) {
        setIsModalLinesZonesOpen(false);
      } else {
        setIsModalLinesZonesOpen(true);
      }
    }
  }, [selectedOption, addLineZone, initialCamerasToEdit]);

  useEffect(() => {
    if (
      !needUpdate ||
      mode !== "view" ||
      !tempConfigID ||
      isRefreshingCameraFrames
    ) {
      return;
    }

    const syncNewCamerasToConfig = async () => {
      try {
        await refreshCamerasInOpenedConfig();
      } finally {
        setNeedUpdate(false);
      }
    };

    syncNewCamerasToConfig();
  }, [
    needUpdate,
    mode,
    tempConfigID,
    isRefreshingCameraFrames,
    refreshCamerasInOpenedConfig,
  ]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      message.destroy(REFRESH_CAMERAS_MESSAGE_KEY);
    };
  }, []);

  useEffect(() => {
    const loadZoneConfigs = async () => {
      try {
        const response = await apiRef.current.getZoneConfigs();
        setZoneConfigs(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    loadZoneConfigs();
    setDrawMode("");
    setConfigState("");

    if (addLineZone) {
      setDrawMode(addLineZone);
      setConfigState("create");
    }
  }, [addLineZone]);

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    if (mode === "view" && !editMode) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    drawMode === "zone"
      ? drawCoordinates(context, zones, editMode, zoneBeingEdited, canvas)
      : drawLineCoordinates(context, lines, editMode, lineBeingEdited, canvas);
  }, [
    drawMode,
    zones,
    lines,
    editMode,
    zoneBeingEdited,
    lineBeingEdited,
    mode,
  ]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getClosestPointOnSegment = useCallback((point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const segmentLengthSquared = dx * dx + dy * dy;

    if (segmentLengthSquared === 0) {
      const distance = Math.hypot(point.x - start.x, point.y - start.y);
      return {
        distance,
        point: { x: start.x, y: start.y },
      };
    }

    const tRaw =
      ((point.x - start.x) * dx + (point.y - start.y) * dy) /
      segmentLengthSquared;
    const t = Math.max(0, Math.min(1, tRaw));
    const projection = {
      x: start.x + t * dx,
      y: start.y + t * dy,
    };
    const distance = Math.hypot(point.x - projection.x, point.y - projection.y);

    return {
      distance,
      point: projection,
    };
  }, []);

  const handleCanvasDoubleClick = useCallback(
    (event) => {
      if (!editMode || drawMode !== "zone" || !canvasRef.current) return;

      const canvasElement = canvasRef.current;
      const rect = canvasElement.getBoundingClientRect();
      const clickPoint = formatCoordinates(
        canvasElement,
        rect,
        event.clientX,
        event.clientY
      );

      const zoneIndex = zones.findIndex((zone) => zone.id === zoneBeingEdited);
      if (zoneIndex < 0) return;

      const zoneToEdit = zones[zoneIndex];
      const zoneVertices = (zoneToEdit.coordinates || [])
        .map((segment) => segment?.[0])
        .filter(
          (vertex) =>
            vertex && Number.isFinite(vertex.x) && Number.isFinite(vertex.y)
        );

      if (zoneVertices.length < 3) return;

      const VERTEX_PROXIMITY_THRESHOLD = 6;
      let closestVertexIndex = -1;
      let closestVertexDistance = Number.POSITIVE_INFINITY;

      zoneVertices.forEach((vertex, vertexIndex) => {
        const distance = Math.hypot(
          vertex.x - clickPoint.x,
          vertex.y - clickPoint.y
        );
        if (distance < closestVertexDistance) {
          closestVertexDistance = distance;
          closestVertexIndex = vertexIndex;
        }
      });

      if (
        closestVertexIndex >= 0 &&
        closestVertexDistance <= VERTEX_PROXIMITY_THRESHOLD
      ) {
        if (zoneVertices.length <= 3) {
          message.warning(
            "A zona precisa manter ao menos 3 vertices. Nao e possivel remover mais."
          );
          return;
        }

        const updatedVertices = [...zoneVertices];
        updatedVertices.splice(closestVertexIndex, 1);

        const rebuiltCoordinatesAfterRemoval = updatedVertices.map(
          (currentVertex, index) => [
            currentVertex,
            updatedVertices[(index + 1) % updatedVertices.length],
          ]
        );

        const updatedZoneAfterRemoval = {
          ...zoneToEdit,
          coordinates: rebuiltCoordinatesAfterRemoval,
        };

        const updatedZonesAfterRemoval = [...zones];
        updatedZonesAfterRemoval[zoneIndex] = updatedZoneAfterRemoval;

        setZones(updatedZonesAfterRemoval);
        setZonestoPost(updatedZoneAfterRemoval);
        return;
      }

      const EDGE_INSERT_THRESHOLD = 6;
      let closestSegmentIndex = -1;
      let closestDistance = Number.POSITIVE_INFINITY;
      let closestProjection = null;

      (zoneToEdit.coordinates || []).forEach((segment, segmentIndex) => {
        const start = segment?.[0];
        const end = segment?.[1];
        if (!start || !end) return;

        const candidate = getClosestPointOnSegment(clickPoint, start, end);
        if (candidate.distance < closestDistance) {
          closestDistance = candidate.distance;
          closestSegmentIndex = segmentIndex;
          closestProjection = candidate.point;
        }
      });

      if (
        closestSegmentIndex < 0 ||
        closestDistance > EDGE_INSERT_THRESHOLD ||
        !closestProjection
      ) {
        return;
      }

      const insertedVertex = {
        x: closestProjection.x,
        y: closestProjection.y,
      };

      const updatedVertices = [...zoneVertices];
      updatedVertices.splice(closestSegmentIndex + 1, 0, insertedVertex);

      const rebuiltCoordinates = updatedVertices.map((currentVertex, index) => [
        currentVertex,
        updatedVertices[(index + 1) % updatedVertices.length],
      ]);

      const updatedZone = {
        ...zoneToEdit,
        coordinates: rebuiltCoordinates,
      };

      const updatedZones = [...zones];
      updatedZones[zoneIndex] = updatedZone;

      setZones(updatedZones);
      setZonestoPost(updatedZone);
    },
    [
      drawMode,
      editMode,
      getClosestPointOnSegment,
      zoneBeingEdited,
      zones,
      setZones,
      setZonestoPost,
    ]
  );

  const initialLoadRef = useRef(false);

  useEffect(() => {
    const fetchZones = async () => {
      if (hasAttemptedFetch || initialLoadRef.current) return;

      try {
        setHasAttemptedFetch(true);
        initialLoadRef.current = true;

        const [zonesResponse, linesResponse] = await Promise.all([
          apiRef.current.getZones(),
          apiRef.current.getLines(),
        ]);

        const dict = zonesResponse.data.map((zone) => {
          const coordinates = zone.vertices
            .map((coord, i, arr) =>
              i < arr.length - 1
                ? [
                    { x: coord[0], y: coord[1] },
                    { x: arr[i + 1][0], y: arr[i + 1][1] },
                  ]
                : []
            )
            .filter((pair) => pair.length > 0);

          if (zone.vertices.length > 1) {
            coordinates.push([
              {
                x: zone.vertices[zone.vertices.length - 1][0],
                y: zone.vertices[zone.vertices.length - 1][1],
              },
              { x: zone.vertices[0][0], y: zone.vertices[0][1] },
            ]);
          }

          return {
            id: zone.value,
            label: zone.label,
            coordinates,
            color: zone.color,
          };
        });

        const linesDict = linesResponse.data.map((line) => {
          const coordinates = line.vertices
            .map((coord, i, arr) =>
              i < arr.length - 1
                ? [
                    { x: coord[0], y: coord[1] },
                    { x: arr[i + 1][0], y: arr[i + 1][1] },
                  ]
                : []
            )
            .filter((pair) => pair.length > 0);

          return {
            id: line.id,
            label: line.description,
            coordinates,
            color: line.color,
          };
        });

        setZones(dict);
        setLines(linesDict);
      } catch (error) {
        console.error("Erro ao buscar zonas ativas:", error);
        initialLoadRef.current = false;
      }
    };

    fetchZones();
  }, [hasAttemptedFetch]);

  const fetchConfigData = useCallback(async () => {
    if (tempConfigID) {
      const config = zoneConfigs.find((c) => c.id === tempConfigID);
      setCurrentConfig(config);
      setEditedDescription(config?.description || "");
    }
  }, [tempConfigID, zoneConfigs]);

  useEffect(() => {
    fetchConfigData();
  }, [fetchConfigData]);

  const handleActivateConfig = (zoneConfig) => {
    const updatedConfigs = zoneConfigs.map((config) =>
      config.id === zoneConfig.id
        ? { ...config, status: true }
        : { ...config, status: false }
    );

    setZoneConfigs(updatedConfigs);

    setTempConfigData(
      updatedConfigs.find((config) => config.id === zoneConfig.id)
    );
  };

  const handleSaveConfig = async () => {
    try {
      if (tempConfigData.id) {
        await updateZoneConfigs(tempConfigData.id, tempConfigData);
      } else {
        await createZoneConfig(tempConfigData);
      }

      const response = await getZoneConfigs();
      setZoneConfigs(response.data);

      message.success("Alterações salvas com sucesso!");
      setTempConfigData(null);
    } catch (error) {
      console.error(error);
    }
  };

  const createAndOpenConfig = async () => {
    if (isCreatingConfig) return;

    try {
      setIsCreatingConfig(true);

      if (configStatus) {
        await deactivateOtherConfigs();
      }

      const createdConfig = await createZoneConfig({
        description: configDescription?.trim() || "",
        status: configStatus,
        creation_date: new Date().toISOString(),
      });

      const response = await getZoneConfigs();
      const refreshedConfigs = response.data;
      const resolvedConfig =
        refreshedConfigs.find((config) => config.id === createdConfig.id) ||
        createdConfig;

      setZoneConfigs(refreshedConfigs);
      setTempConfigID(resolvedConfig.id);
      setCurrentConfig(resolvedConfig);
      setEditedDescription(resolvedConfig.description || "");
      setTempConfigData(null);
      setConfigDescription("");
      setConfigStatus(true);
      setConfigState("");
      setDrawMode("");
      setIsOpenModalZoneConfig(false);
      setMode("view");

      await viewConfig(resolvedConfig.id);

      message.success("Configuração criada com sucesso!");
    } catch (error) {
      console.error("Erro ao criar configuração:", error);
      message.error("Erro ao criar configuração");
    } finally {
      setIsCreatingConfig(false);
    }
  };

  const handleSelectChange = (value) => {
    if (value.includes("all")) {
      if (camerasToEdit.length === cameras.length) {
        setCamerasToEdit([]);
      } else {
        setCamerasToEdit(cameras.map((camera) => camera.id));
      }
    } else {
      setCamerasToEdit(value);
    }
  };

  const handleUpdateDescription = async () => {
    try {
      await updateZoneConfigs(currentConfig.id, {
        ...currentConfig,
        description: editedDescription,
      });

      setCurrentConfig({
        ...currentConfig,
        description: editedDescription,
      });

      const response = await getZoneConfigs();
      setZoneConfigs(response.data);

      setIsEditingDescription(false);
      setIsHovering(false);
    } catch (error) {
      console.error(error);
    }
  };

  const table_columns = [
    {
      title: "Descrição",
      dataIndex: "description",
      key: "description",
      align: "center",
      render: (descricao, record) => (
        <div>
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            {descricao}
          </div>
          <Flex
            justify="center"
            align="center"
            style={{
              whiteSpace: "nowrap",
            }}>
            <Space size="small" style={{ marginTop: 8 }}>
              <Tooltip placement="bottom" title={"Duplicar"}>
                <Button
                  icon={<CopyOutlined style={{ fontSize: "16px" }} />}
                  type="text"
                  onClick={async () => {
                    try {
                      await duplicateconfig(record.id);
                      message.success("Configuração duplicada com sucesso!");
                    } catch (error) {
                      message.error("Erro ao duplicar configuração");
                      console.error(error);
                    }
                  }}
                />
              </Tooltip>
              <Tooltip placement="bottom" title={"Vizualizar e editar"}>
                <Button
                  icon={<EyeOutlined style={{ fontSize: "16px" }} />}
                  type="text"
                  onClick={() => {
                    setMode("view");
                    viewConfig(record.id);
                    setTempConfigID(record.id);
                  }}
                />
              </Tooltip>
              <Tooltip
                title={infoText(record.id)}
                placement="bottom"
                overlayStyle={{ maxWidth: 200 }}>
                <InfoCircleOutlined
                  style={{
                    fontSize: "15px",
                    cursor: "pointer",
                    marginLeft: "4px",
                  }}
                />
              </Tooltip>
            </Space>
          </Flex>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Button
          type={record.status ? "primary" : "default"}
          onClick={() => {
            handleActivateConfig(record);
          }}
          disabled={record.status}
          style={{
            backgroundColor: record.status ? "#e9ffce" : "#ffc7c7",
            borderColor: record.status ? "#5c893c" : "#9d4d4d",
          }}>
          {record.status ? "Ativa" : "Inativa"}
        </Button>
      ),
      align: "center",
    },
  ];

  const zones_table_columns = [
    {
      title: "Zona/Linha",
      dataIndex: "description",
      key: "type",
    },
    {
      title: "Identificação",
      dataIndex: "identification",
      key: "identification",
      render: (text, record) => (
        <Avatar
          shape="square"
          style={{
            width: "30%",
            backgroundColor: record.identification,
          }}
        />
      ),
    },
    {
      title: "Zona Staff",
      dataIndex: "is_staff_zone",
      key: "is_staff_zone",
      render: (value, record) => {
        if (record.type !== "zone") return null;
        return (
          <Checkbox
            checked={!!value}
            onChange={async (e) => {
              const checked = e.target.checked;
              try {
                await patchZoneIsStaff(record.id, checked);
                setZonesData((prev) =>
                  prev.map((z) =>
                    z.id === record.id && z.type === "zone"
                      ? { ...z, is_staff_zone: checked }
                      : z
                  )
                );
              } catch {
                message.error("Não foi possível atualizar a zona.");
              }
            }}
          />
        );
      },
    },
    {
      title: "Zona Fila",
      dataIndex: "is_fila_zone",
      key: "is_fila_zone",
      render: (value, record) => {
        if (record.type !== "zone") return null;
        return (
          <Checkbox
            checked={!!value}
            onChange={async (e) => {
              const checked = e.target.checked;
              try {
                await patchZoneIsFila(record.id, checked);
                setZonesData((prev) =>
                  prev.map((z) =>
                    z.id === record.id && z.type === "zone"
                      ? { ...z, is_fila_zone: checked }
                      : z
                  )
                );
              } catch {
                message.error("Não foi possível atualizar a zona.");
              }
            }}
          />
        );
      },
    },
    {
      title: "Editar",
      dataIndex: "edit",
      key: "edit",
      render: (text, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            stopAddingInCurrentView();
            if (record.type === "zone") {
              setDrawMode("zone");
              const filteredZones = zonesData.filter(
                (zone) =>
                  zone.camera_id === selectedCameraId &&
                  zone.configuration === record.configuration
              );

              const updatedZones = filteredZones.map((zone) => {
                const points = zone.coordinates.map((point) => ({
                  x: point[0] * canvas.width,
                  y: point[1] * canvas.height,
                }));

                const coordinates = [];
                for (let i = 0; i < points.length; i++) {
                  const currentPoint = points[i];
                  const nextPoint = points[(i + 1) % points.length];
                  coordinates.push([currentPoint, nextPoint]);
                }

                const convertedZone = {
                  id: zone.id,
                  label: zone.description,
                  description: zone.description,
                  configuration: zone.configuration,
                  camera_id: zone.camera_id,
                  color: zone.identification,
                  coordinates,
                  is_staff_zone: zone.is_staff_zone || false,
                  is_fila_zone: zone.is_fila_zone || false,
                };
                return convertedZone;
              });
              setZones(updatedZones);
              setEditedZoneDescription(record.description || "");

              handleEditZone(
                record.id,
                setEditMode,
                setZoneBeingEdited,
                updatedZones,
                setBackupCoord
              );
            } else {
              setDrawMode("line");
              const filteredLines = zonesData.filter(
                (line) =>
                  line.camera_id === selectedCameraId &&
                  line.configuration === record.configuration
              );

              const updatedLines = filteredLines.map((line) => {
                const convertedLine = {
                  id: line.id,
                  label: line.description,
                  configuration: line.configuration,
                  camera_id: line.camera_id,
                  color: line.identification,
                  coordinates: [
                    [
                      {
                        x: line.coordinates[0][0] * canvas.width,
                        y: line.coordinates[0][1] * canvas.height,
                      },
                      {
                        x: line.coordinates[1][0] * canvas.width,
                        y: line.coordinates[1][1] * canvas.height,
                      },
                    ],
                  ],
                  direction: line.direction,
                };
                return convertedLine;
              });

              setLines(updatedLines);
              handleEditLine(
                record.id,
                setEditMode,
                setLineBeingEdited,
                updatedLines,
                setBackupCoord
              );
            }
          }}
        />
      ),
    },
    {
      title: "Remover",
      dataIndex: "remove",
      key: "remove",
      render: (text, record) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() => {
            setType(record.type);
            setZoneToDelete(record);
            setIsModalDeleteOpen(true);
          }}
        />
      ),
    },
  ];

  const postNewConfigs = async () => {
    const newConfigs = zoneConfigs.filter((config) => config.isNew);

    try {
      if (newConfigs.some((config) => config.status)) {
        await deactivateOtherConfigs();
      }

      await Promise.all(
        newConfigs.map(async (config) => {
          const data = {
            description: config.description,
            status: config.status,
            creation_date: config.creation_date,
          };

          try {
            await postConfigs(data);
          } catch (error) {
            console.error(
              `Erro ao postar configuração ${config.description}:`,
              error
            );
            throw error;
          }
        })
      );

      setZoneConfigs((prevConfigs) =>
        prevConfigs.map((config) =>
          config.isNew ? { ...config, isNew: false } : config
        )
      );
    } catch (error) {
      console.error("Erro ao processar configurações:", error);
      message.error("Erro ao salvar configurações");
    }
  };

  const [messageApi, contextHolder] = message.useMessage();
  const zoneCrossWarningMessage =
    "Aviso: a zona em edi\u00E7\u00E3o cruza outra zona. A finaliza\u00E7\u00E3o fica bloqueada at\u00E9 a corre\u00E7\u00E3o.";
  const isEditedZoneCrossing = useMemo(() => {
    if (!editMode || drawMode !== "zone") return false;
    const zoneInEdition = zones.find((zone) => zone.id === zoneBeingEdited);
    if (!zoneInEdition) return false;
    return checkZoneCrossOther(zoneInEdition, zones);
  }, [editMode, drawMode, zones, zoneBeingEdited]);

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          components: {
            Card: {
              headerBg: "var(--ouran-card-header-bg)",
            },
            Collapse: {
              headerBg: "var(--ouran-card-header-bg)",
            },
          },
        }}>
        <Flex
          style={{ width: "100%" }}
          justify="center"
          align="center"
          vertical
          gap={20}>
          {mode === "" ? (
            <>
              {configState === "" && !addLineZone && (
                <>
                  <Card
                    title="Gerencie as zonas e câmeras da sua loja!"
                    style={{
                      width: "90%",
                      backgroundColor: "var(--ouran-card-header-bg)",
                    }}>
                    <Row>
                      <Col span={24}>
                        <Text style={{ fontSize: 16 }} strong>
                          O que você deseja fazer?
                        </Text>
                      </Col>
                    </Row>
                  </Card>

                  <Row>
                    <Col span={16}>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          flexWrap: "nowrap",
                          justifyContent: "flex-start",
                          width: "100%",
                        }}>
                        {cardOptions
                          .filter(
                            ({ adminOnly }) => !adminOnly || isAdministrator
                          )
                          .map(({ value, label, icon }) => (
                            <Button
                              key={value}
                              type={
                                selectedOption === value ? "primary" : "default"
                              }
                              onClick={() => {
                                setSelectedOption(value);
                                if (value === "edit_zone") {
                                  setIsSecondPhaseTutorialOpen(true);
                                }
                              }}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "8px 12px",
                                textAlign: "center",
                                height: 70,
                              }}>
                              <div style={{ fontSize: 20, marginBottom: 4 }}>
                                {icon}
                              </div>

                              <div
                                style={{
                                  fontSize: 14,
                                  whiteSpace: "nowrap",
                                }}>
                                {label}
                              </div>
                            </Button>
                          ))}
                      </div>
                    </Col>
                  </Row>
                </>
              )}

              {selectedOption === "edit_zone" ? (
                isLoading || isCamerasLoading ? (
                  <Spin size="large" />
                ) : ableToEditZones || ableToEditLines ? (
                  readyToEditZones || readyToEditLines ? (
                    <EditZones
                      camerasToEdit={camerasToEdit}
                      drawMode={drawMode}
                      existingConfigId={existingConfigId || tempConfigID}
                      shouldShowTutorialBool={shouldShowTutorialBool}
                      isFifthPhaseTutorialOpen={isFifthPhaseTutorialOpen}
                      setIsFifthPhaseTutorialOpen={setIsFifthPhaseTutorialOpen}
                    />
                  ) : (
                    <>
                      {configState === "" ? (
                        <>
                          <p
                            style={{
                              fontSize: "18px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}>
                            Selecione a configuração de zonas que deseja
                            utilizar
                          </p>
                          <>
                            {zoneConfigs.length > 0 ? (
                              <>
                                <Table
                                  columns={table_columns}
                                  dataSource={sortedData}
                                  style={{ width: "40%" }}
                                  rowKey={(record) => record.id}
                                />
                                <Row
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                  }}>
                                  <Col>
                                    <Button
                                      ref={tutorialRefs.addConfig}
                                      type="default"
                                      onClick={() =>
                                        setIsOpenModalZoneConfig(true)
                                      }>
                                      Adicionar uma configuração nova
                                    </Button>
                                  </Col>
                                  <Button
                                    type="primary"
                                    onClick={handleSaveConfig}
                                    disabled={!tempConfigData}>
                                    Pronto
                                  </Button>
                                  <Col></Col>
                                </Row>
                              </>
                            ) : (
                              <div style={{ textAlign: "center" }}>
                                <p>Nenhuma configuração de zona encontrada.</p>
                                <Button
                                  type="primary"
                                  onClick={() =>
                                    setIsOpenModalZoneConfig(true)
                                  }>
                                  Criar nova configuração
                                </Button>
                              </div>
                            )}
                          </>
                        </>
                      ) : configState === "create" ? (
                        <>
                          <Modal
                            title="Escolha uma opção"
                            open={isModalLinesZonesOpen}
                            onCancel={() => setIsModalLinesZonesOpen(false)}
                            footer={null}
                            closable={false}>
                            <div ref={tutorialRefs.zoneOptions}>
                              <Button
                                type="primary"
                                onClick={handleOption1}
                                style={{ marginBottom: "10px", width: "100%" }}>
                                Cadastrar entrada da loja
                              </Button>
                              <Button
                                type="primary"
                                onClick={handleOption2}
                                style={{ width: "100%" }}>
                                Cadastrar zonas
                              </Button>
                            </div>
                          </Modal>
                          <Card
                            style={{
                              width: "50%",
                              border: "2px solid var(--ouran-card-header-bg)",
                              padding: "-5px",
                            }}>
                            <p style={{ fontSize: "18px" }}>
                              <ExclamationCircleOutlined
                                style={{
                                  color: "#eead2d",
                                  marginRight: "15px",
                                  fontSize: "24px",
                                }}
                              />
                              {`Você está no modo de cadastro ${
                                drawMode === "line"
                                  ? "da entrada da loja"
                                  : "de zonas"
                              }`}
                            </p>
                          </Card>
                          <Card
                            ref={tutorialRefs.cameraSelector}
                            title="Selecione quais câmeras deseja editar"
                            style={{
                              width: "80%",
                              backgroundColor: "var(--ouran-card-header-bg)",
                            }}
                            loading={cameras.length ? false : true}>
                            <Row gutter={[16, 16]}>
                              <Col span={24}>
                                <Select
                                  mode="multiple"
                                  style={{ width: "100%" }}
                                  options={[
                                    { value: "all", label: "Todos" },
                                    ...cameras.map((camera) => ({
                                      value: camera.id,
                                      label: camera.name,
                                    })),
                                  ]}
                                  placeholder="Selecione as câmeras"
                                  maxTagCount={"responsive"}
                                  allowClear
                                  onChange={handleSelectChange}
                                  value={camerasToEdit}
                                />
                              </Col>
                              <Col span={24} align="center">
                                <Button
                                  type="primary"
                                  disabled={camerasToEdit.length ? false : true}
                                  onClick={async () => {
                                    await postNewConfigs();
                                    setIsFourthPhaseTutorialOpen(false);
                                    setIsFifthPhaseTutorialOpen(true);
                                    if (drawMode === "line") {
                                      setReadyToEditLines(true);
                                    } else {
                                      setReadyToEditZones(true);
                                    }
                                  }}>
                                  Iniciar edição
                                </Button>
                              </Col>
                            </Row>
                          </Card>
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  )
                ) : errorTypeToEditZones !== "" ? (
                  <>
                    <Empty />
                    <Text strong>
                      {`Para editar as zonas da loja, é necessário que haja ${
                        errorTypeToEditZones === "collections"
                          ? "categorias"
                          : "câmeras"
                      }
                  cadastradas no sistema.`}
                    </Text>
                    {errorTypeToEditZones === "collections" ? (
                      <Text>
                        Acesse:{" "}
                        <Link href="/events_manager">Adicionar Eventos</Link>{" "}
                        <ArrowRightOutlined /> Registrar uma nova categoria
                      </Text>
                    ) : (
                      <Text>
                        Acesse essa mesma página na aba `&ldquo;`Gerenciar
                        câmeras`&ldquo;` para adicionar câmeras
                      </Text>
                    )}
                  </>
                ) : errorTypeToEditLines !== "" ? (
                  <>
                    <Empty />
                    <Text strong>
                      {`Para editar as linhas da loja, é necessário que haja câmeras
                  cadastradas no sistema.`}
                    </Text>
                    <Text>
                      Acesse essa mesma página na aba `&ldquo;`Gerenciar
                      câmeras`&ldquo;` para adicionar câmeras
                    </Text>
                  </>
                ) : (
                  <></>
                )
              ) : selectedOption === "add_cameras" ? (
                <>
                  <Collapse
                    ref={tutorialRefs.addCamera}
                    style={{ width: "50%" }}
                    items={[
                      {
                        key: "1",
                        label: <Text strong>Adicionar uma câmera</Text>,
                        children: (
                          <CreateCamera
                            setUpdate={setNeedUpdate}
                            messageApi={messageApi}
                          />
                        ),
                      },
                    ]}
                    defaultActiveKey={["1"]}
                  />
                  <Collapse
                    activeKey={shouldOpenCameraTable ? ["2"] : []}
                    ref={tutorialRefs.cameraList}
                    onChange={(keys) => {
                      setShouldOpenCameraTable(keys.includes("2"));
                    }}
                    style={{
                      width: "50%",
                      padding: 0,
                    }}
                    items={[
                      {
                        key: "2",
                        label: (
                          <Text
                            strong
                            style={{ height: "100%", width: "100%" }}>
                            Visualizar câmeras cadastradas
                          </Text>
                        ),
                        children:
                          cameras.length > 0 ? (
                            <ListCameras
                              update={needUpdate}
                              setUpdate={setNeedUpdate}
                            />
                          ) : (
                            <Empty description="Nenhuma câmera cadastrada" />
                          ),
                      },
                    ]}
                  />
                </>
              ) : selectedOption === "calibrate_cameras" ? (
                <div style={{ width: "100%", marginTop: 8 }}>
                  <iframe
                    ref={calibratorRef}
                    src={(() => {
                      const p = new URLSearchParams();
                      if (user?.active_branch_id)
                        p.set("branch_id", String(user.active_branch_id));
                      return `${calibratorOrigin}/?${p.toString()}`;
                    })()}
                    onLoad={sendCalibratorToken}
                    title="Calibrador de câmeras"
                    style={{
                      width: "100%",
                      height: "calc(100vh - 220px)",
                      border: "none",
                      borderRadius: 8,
                    }}
                    allow="camera"
                  />
                </div>
              ) : selectedOption === "view_cameras" ? (
                <></>
              ) : (
                <></>
              )}
              <Modal
                open={isOpenModalZoneConfig}
                width="fit-content"
                title="Nova configuração de zonas"
                align="center"
                confirmLoading={isCreatingConfig}
                onOk={createAndOpenConfig}
                okButtonProps={{
                  icon: <EditOutlined ref={tutorialRefs.saveConfig} />,
                  color: "red",
                }}
                onCancel={() => {
                  setConfigDescription("");
                  setConfigStatus(true);
                  setIsOpenModalZoneConfig(false);
                }}
                icon={<EditOutlined />}>
                <Card ref={tutorialRefs.configForm}>
                  <div>
                    <Text strong style={{ marginTop: 0, marginBottom: 20 }}>
                      Escolha uma descrição para a nova configuração
                    </Text>
                    <br />
                    <Input
                      value={configDescription}
                      placeholder="Descrição"
                      onChange={(e) => setConfigDescription(e.target.value)}
                      style={{ width: "75%" }}
                    />
                    <br />
                    <InfoCircleFilled />
                    <Text style={{ marginLeft: 5 }}>
                      Deixe em branco para uma descrição padrão
                    </Text>
                  </div>
                  <br />
                  <div>
                    <Text strong style={{ marginTop: 0, marginBottom: 20 }}>
                      Qual o status da nova configuração?
                    </Text>
                    <br />
                    <Select
                      value={configStatus}
                      onChange={(value) => {
                        setConfigStatus(value);
                      }}
                      style={{ width: "75%" }}
                      options={[
                        { label: "Ativa", value: true },
                        { label: "Inativa", value: false },
                      ]}
                    />
                    <br />
                  </div>
                </Card>
              </Modal>
            </>
          ) : mode === "edit" ? (
            <></>
          ) : mode === "view" ? (
            <>
              <Card
                title={"Confira as zonas e linhas dessa configuração"}
                align="center"
                style={{ width: "75%" }}>
                {currentCamera?.camera_name && (
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "8px",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#333",
                    }}>
                    {currentCamera.camera_name}
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}>
                  {(isImageLoading || isEditingLoading || isSavingNewShape) && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        zIndex: 1,
                      }}>
                      <Spin size="large" />
                    </div>
                  )}
                  <canvas
                    ref={canvasRef}
                    onClick={
                      isAddingInView && !editMode ? handleAddShapeClick : null
                    }
                    onMouseMove={
                      editMode && isPressed
                        ? (event) =>
                            handleMoveVertice(
                              event,
                              canvas,
                              drawMode,
                              zones,
                              zoneBeingEdited,
                              coordIndex,
                              pointIndex,
                              editMode,
                              setZones,
                              lines,
                              lineBeingEdited,
                              setLines,
                              setZonestoPost
                            )
                        : isAddingInView
                          ? handleAddShapeMouseMove
                          : null
                    }
                    onMouseDownCapture={
                      editMode
                        ? (event) =>
                            handleMoveVerticeClick(
                              event,
                              canvas,
                              zones,
                              coordIndex,
                              setCoordIndex,
                              setPointIndex,
                              setIsPressed,
                              drawMode,
                              zoneBeingEdited,
                              lines,
                              lineBeingEdited
                            )
                        : null
                    }
                    onMouseUpCapture={
                      editMode
                        ? (event) =>
                            handleMoveVerticeClick(
                              event,
                              canvas,
                              zones,
                              coordIndex,
                              setCoordIndex,
                              setPointIndex,
                              setIsPressed,
                              drawMode,
                              zoneBeingEdited,
                              lines,
                              lineBeingEdited
                            )
                        : null
                    }
                    onMouseLeave={
                      editMode
                        ? () => {
                            setIsPressed(false);
                            setCoordIndex([]);
                            setPointIndex([]);
                          }
                        : null
                    }
                    onDoubleClick={
                      editMode && drawMode === "zone"
                        ? handleCanvasDoubleClick
                        : null
                    }
                    style={{
                      backgroundImage: currentCamera?.camera_image
                        ? `url("data:image/jpeg;base64,${currentCamera.camera_image}")`
                        : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
                {!editMode ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                        marginBottom: "10px",
                        gap: "10px",
                      }}>
                      {isEditingDescription ? (
                        <>
                          <Input
                            value={editedDescription}
                            onChange={(e) =>
                              setEditedDescription(e.target.value)
                            }
                            style={{ width: "300px" }}
                            onPressEnter={() => {
                              if (editedDescription.trim()) {
                                handleUpdateDescription();
                                setIsHovering(false);
                              }
                            }}
                          />
                          <Button
                            type="primary"
                            onClick={handleUpdateDescription}
                            disabled={!editedDescription.trim()}>
                            Salvar
                          </Button>
                          <Button
                            onClick={() => {
                              setIsEditingDescription(false);
                              setEditedDescription(
                                currentConfig?.description || ""
                              );
                              setIsHovering(false);
                            }}>
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <Tooltip title="Clique para editar">
                          <Button
                            type="text"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#f0f0f0";
                              setIsHovering(true);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              setIsHovering(false);
                            }}
                            onClick={() => setIsEditingDescription(true)}
                            style={{
                              padding: "4px 12px",
                              display: "flex",
                              alignItems: "center",
                            }}>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                              }}>
                              {currentConfig?.description}
                              <EditOutlined
                                style={{
                                  opacity: isHovering ? 1 : 0,
                                  transition: "opacity 0.3s",
                                  marginLeft: "8px",
                                }}
                              />
                            </span>
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                    <Table
                      columns={zones_table_columns}
                      locale={{
                        emptyText:
                          "Nenhuma zona ou linha cadastrada para esta câmera",
                      }}
                      dataSource={filteredZonesAndLines}
                      pagination={false}
                      style={{ width: "90%", marginBottom: "50px" }}
                      rowKey={(record) => record.id}
                    />
                    {isAddingInView && (
                      <Card
                        style={{
                          width: "90%",
                          margin: "0 auto 16px auto",
                          border: "1px solid var(--ouran-card-header-bg)",
                        }}>
                        <Text strong>
                          {addTargetType === "zone"
                            ? "Modo adicionar zona: clique para criar os vertices e feche clicando perto do primeiro ponto."
                            : awaitingArrowSelection
                              ? "Clique na seta voltada para o lado interno da loja para definir o sentido da entrada."
                              : "Modo adicionar linha: clique no ponto inicial e no ponto final. Depois, clique na seta do lado interno da loja."}
                        </Text>
                      </Card>
                    )}
                    <Row justify="center" style={{ marginBottom: "8px" }}>
                      <Col>
                        <Button
                          type="primary"
                          loading={isRefreshingCameraFrames}
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            transition: "all 0.2s ease",
                            boxShadow: "none",
                          }}
                          onMouseEnter={handleRefreshButtonMouseEnter}
                          onMouseLeave={handleRefreshButtonMouseLeave}
                          onMouseDown={handleRefreshButtonMouseDown}
                          onMouseUp={handleRefreshButtonMouseUp}
                          onClick={refreshCamerasInOpenedConfig}
                          disabled={
                            !tempConfigID ||
                            isSavingNewShape ||
                            isRefreshingCameraFrames
                          }>
                          Atualizar cameras
                        </Button>
                      </Col>
                    </Row>
                    <Row
                      justify="center"
                      style={{ marginBottom: "16px" }}
                      gutter={[8, 8]}>
                      <Col>
                        <Button
                          type="primary"
                          onClick={() => startAddToCurrentConfig("zone")}
                          disabled={
                            !selectedCameraId || editMode || isSavingNewShape
                          }>
                          Adicionar nova zona nesta câmera
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          type="primary"
                          onClick={() => startAddToCurrentConfig("line")}
                          disabled={
                            !selectedCameraId || editMode || isSavingNewShape
                          }>
                          Adicionar nova linha nesta câmera
                        </Button>
                      </Col>
                      {isAddingInView && (
                        <Col>
                          <Button onClick={stopAddingInCurrentView}>
                            Finalizar inclusao
                          </Button>
                        </Col>
                      )}
                    </Row>
                    <Row
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}>
                      <Col>
                        <Button
                          type="default"
                          onClick={showPreviousCamera}
                          style={{ marginRight: "10px" }}
                          disabled={editMode || isAddingInView}>
                          <ArrowLeftOutlined />
                          Câmera Anterior
                        </Button>
                        <Button
                          type="default"
                          onClick={showNextCamera}
                          disabled={editMode || isAddingInView}>
                          Próxima câmera
                          <ArrowRightOutlined />
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          type="default"
                          onClick={() => {
                            stopAddingInCurrentView();
                            setMode("");
                          }}
                          disabled={editMode || isSavingNewShape}>
                          <ArrowLeftOutlined />
                          Voltar
                        </Button>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <Flex
                    flexDirection="column"
                    align="center"
                    justify="center"
                    style={{
                      width: "100%",
                      marginTop: "20px",
                      marginBottom: "20px",
                    }}
                    gap="50px">
                    {drawMode === "zone" && (
                      <Flex
                        vertical
                        gap="8px"
                        style={{ width: "100%", maxWidth: "420px" }}>
                        <Text strong>Nome da zona</Text>
                        <Input
                          value={editedZoneDescription}
                          onChange={(event) =>
                            setEditedZoneDescription(event.target.value)
                          }
                          placeholder="Nome da zona"
                        />
                      </Flex>
                    )}
                    <Button
                      type="primary"
                      disabled={
                        isEditingLoading ||
                        (drawMode === "zone" &&
                          !editedZoneDescription.trim()) ||
                        (drawMode === "zone" && isEditedZoneCrossing)
                      }
                      loading={isEditingLoading}
                      onClick={async () => {
                        if (drawMode === "zone" && isEditedZoneCrossing) {
                          message.warning(zoneCrossWarningMessage);
                          return;
                        }
                        setIsEditingLoading(true);
                        try {
                          const zoneDraft =
                            drawMode === "zone"
                              ? zonestoPost?.id
                                ? {
                                    ...zonestoPost,
                                    description: editedZoneDescription.trim(),
                                  }
                                : {
                                    ...zones.find(
                                      (zone) => zone.id === zoneBeingEdited
                                    ),
                                    description: editedZoneDescription.trim(),
                                  }
                              : zonestoPost;
                          setEditMode(false);
                          drawMode === "zone"
                            ? setZoneBeingEdited(-1)
                            : setLineBeingEdited(-1);
                          setZones([]);
                          setLines([]);
                          await updateZones(zoneDraft);
                          setHasAttemptedFetch(false);
                        } finally {
                          setIsEditingLoading(false);
                        }
                      }}>
                      Finalizar edição
                    </Button>
                    {drawMode === "zone" && isEditedZoneCrossing && (
                      <Text type="warning" strong>
                        {zoneCrossWarningMessage}
                      </Text>
                    )}
                    <Button
                      loading={isEditingLoading}
                      onClick={async () => {
                        setIsEditingLoading(true);
                        try {
                          setZones([]);
                          setLines([]);
                          setEditMode(false);
                          setEditedZoneDescription("");
                          await viewConfig(tempConfigID, true);
                        } finally {
                          setIsEditingLoading(false);
                        }
                      }}>
                      Cancelar edição
                    </Button>
                  </Flex>
                )}
              </Card>
              <Modal
                open={isModalDeleteOpen}
                width="fit-content"
                title={`Você tem certeza que deseja excluir essa ${type === "line" ? "linha" : "zona"}?`}
                onOk={handleDeleteZone}
                onCancel={() => {
                  setIsModalDeleteOpen(false);
                  setZoneToDelete(null);
                  setZoneToDelete(null);
                }}
                closable={false}>
                <p>Essa ação não pode ser desfeita.</p>
              </Modal>
            </>
          ) : null}
          <Modal
            open={isNewShapeModalOpen}
            title={`Nova ${pendingNewShape?.type === "line" ? "linha" : "zona"}`}
            okText="Salvar"
            cancelText="Cancelar"
            confirmLoading={isSavingNewShape}
            onOk={handleSaveNewShape}
            onCancel={() => {
              setIsNewShapeModalOpen(false);
              setPendingNewShape(null);
              setNewShapeDescription("");
            }}>
            <Flex vertical gap={8}>
              <Text strong>Descricao</Text>
              <Input
                value={newShapeDescription}
                placeholder="Descricao"
                onChange={(event) => setNewShapeDescription(event.target.value)}
              />
              <Text type="secondary">
                Deixe em branco para usar a descricao padrao.
              </Text>
            </Flex>
          </Modal>
        </Flex>
        <Tour
          open={shouldShowTutorialBool && isFirstPhaseTutorialOpen}
          steps={steps.slice(0, 4)}
          onClose={() => {
            setIsFirstPhaseTutorialOpen(false);
          }}
          gap={{
            offset: offsetGap,
            radius: 10,
          }}
        />
        <Tour
          open={
            shouldShowTutorialBool &&
            selectedOption === "edit_zone" &&
            isSecondPhaseTutorialOpen
          }
          steps={steps.slice(4, 6)}
          onClose={() => {
            setIsSecondPhaseTutorialOpen(false);
          }}
          gap={{
            offset: offsetGap,
            radius: 10,
          }}
        />
        <Tour
          open={shouldShowTutorialBool && isThirdPhaseTutorialOpen}
          steps={steps.slice(7, 8)}
          onClose={() => {
            setIsThirdPhaseTutorialOpen(false);
          }}
          gap={{
            offset: offsetGap,
            radius: 10,
          }}
        />
        <Tour
          open={shouldShowTutorialBool && isFourthPhaseTutorialOpen}
          steps={steps.slice(8, 9)}
          onClose={() => {
            setIsFourthPhaseTutorialOpen(false);
          }}
          gap={{
            offset: offsetGap,
            radius: 10,
          }}
        />
      </ConfigProvider>
    </>
  );
}

export default ZonesManager;
